import { useNavigate } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import BottomNav from "@/components/BottomNav";
import BackupSettings from "@/components/BackupSettings";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Full-page host for the backup/import UI, reached from Settings →
 * "Backup options". Keeps the Settings page clean by pushing the two
 * chunky sections (native JSON export/import + cross-platform importers)
 * behind a dedicated route, matching the pattern used by /reset-password.
 *
 * Gated to admin during the tester phase: the feature is new and we want
 * to shake it out ourselves before turning it on for the public.
 */
export default function BackupOptions() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  return (
    <div className="mx-auto max-w-2xl px-4 pb-32 pt-safe-6">
      <header className="mb-6 flex items-center gap-3">
        <button
          data-testid="backup-options-back-btn"
          onClick={() => nav(-1)}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <div className="font-display text-xs uppercase tracking-[0.35em] text-violet-400/80">Your data</div>
          <h1 className="font-display text-3xl leading-tight text-white">Backup options</h1>
        </div>
      </header>

      {!isAdmin ? (
        <div
          data-testid="backup-coming-soon"
          className="rounded-2xl border border-amber-400/30 bg-amber-500/10 p-6 text-center"
        >
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/20 text-amber-200">
            <Clock size={22} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-medium">Coming soon</div>
          <h2 className="mt-1 font-display text-xl text-white">Backups &amp; imports are in testing</h2>
          <p className="mt-2 text-sm text-amber-100/90 leading-relaxed">
            We're still hardening data export, restore, and cross-platform imports (Character.AI, Chub, JanitorAI, SillyTavern).
            This will open up to everyone right after our own testing wraps.
          </p>
        </div>
      ) : (
        <>
          <p className="mb-6 text-sm text-neutral-400">
            Save a copy of everything you've made, restore an older backup, or import characters from another platform.
          </p>
          <BackupSettings />
        </>
      )}

      <BottomNav />
    </div>
  );
}
